import {ActionMenu} from '@primer/react-brand'
import {useMemo} from 'react'
import {OS, type OSItem, resolveByOS, resolveSupportedOS, sortByOS} from '@github-ui/get-os'
import {useArchitecture} from '@github-ui/swp-core/lib/hooks/use-architecture'
import {useDetectedOS} from '@github-ui/swp-core/lib/hooks/use-detected-os'
import {getAnalyticsEvent} from '@github-ui/swp-core/lib/utils/analytics'

export interface InstallerItem extends OSItem {
  label: string
  href: string
}

export interface InstallerActionMenuProps {
  items: InstallerItem[]
  initialOS?: OS
  location?: string
}

// The Copilot app only ships desktop builds (macOS, Windows, Linux). Map the
// detected OS onto one of those. Mobile and unknown platforms (iOS, Android,
// Unknown) have no matching installer, so fall back to macOS as the generic
// default rather than silently bucketing them into Linux.
export const resolveDesktopOS = (detectedOS: OS): Extract<OS, 'macOS' | 'Windows' | 'Linux'> =>
  resolveSupportedOS(detectedOS, [OS.macOS, OS.Windows, OS.Linux])

export const resolveShowcaseOS = (detectedOS: OS): Extract<OS, 'macOS' | 'Windows'> => {
  return resolveDesktopOS(detectedOS) === OS.macOS ? OS.macOS : OS.Windows
}

export const InstallerActionMenu = ({items, initialOS, location}: InstallerActionMenuProps) => {
  const detectedOS = useDetectedOS(initialOS)
  const resolvedArch = useArchitecture()

  const resolvedOS = useMemo(() => resolveDesktopOS(detectedOS), [detectedOS])

  const resolvedInstaller = useMemo(
    () => resolveByOS(items, resolvedOS, resolvedArch),
    [items, resolvedOS, resolvedArch],
  )

  const sortedInstallerItems = useMemo(
    () => sortByOS(items, resolvedOS, resolvedArch),
    [items, resolvedOS, resolvedArch],
  )

  return (
    <ActionMenu mode="split-button">
      <ActionMenu.Button variant="primary" as="a" href={resolvedInstaller?.href}>
        {resolvedInstaller?.label}
      </ActionMenu.Button>
      <ActionMenu.Overlay aria-label={resolvedInstaller?.label || ''}>
        {sortedInstallerItems.map(item => (
          <ActionMenu.Item
            key={item.label}
            as="a"
            href={item.href}
            {...getAnalyticsEvent({action: item.label, tag: 'a', context: 'installer', location})}
          >
            {item.label}
          </ActionMenu.Item>
        ))}
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}
